import {
    createContext,
    createElement,
    useCallback,
    useContext,
    useMemo,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from "react";
import { useMediaQuery } from "@mui/material";
import { useLocalStorage } from "./persistent-state";

type DarkModeState = readonly [boolean, Dispatch<SetStateAction<boolean>>];

const DarkModeContext = createContext<DarkModeState | undefined>(undefined);

/**
 * Dark mode state: the user's persisted choice, falling back to the OS
 * `prefers-color-scheme` setting until they toggle it explicitly.
 */
export function useDarkModeState(): DarkModeState {
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const [storedDarkMode, setStoredDarkMode] =
        useLocalStorage<boolean>("global-darkmode");

    const darkMode = storedDarkMode ?? prefersDarkMode;

    const setDarkMode = useCallback<Dispatch<SetStateAction<boolean>>>(
        (value) => {
            setStoredDarkMode((prev) =>
                typeof value === "function"
                    ? value(prev ?? prefersDarkMode)
                    : value
            );
        },
        [setStoredDarkMode, prefersDarkMode]
    );

    return useMemo(
        () => [darkMode, setDarkMode] as const,
        [darkMode, setDarkMode]
    );
}

/**
 * Shares one dark mode state across the app. Must wrap the ThemeProvider so
 * toggles anywhere in the tree re-render it. (`useLocalStorage` instances
 * don't sync with each other within a tab, so context is required.)
 */
export function DarkModeProvider({ children }: { children?: ReactNode }) {
    return createElement(
        DarkModeContext.Provider,
        { value: useDarkModeState() },
        children
    );
}

/**
 * Dark mode state shared via DarkModeProvider. Falls back to a standalone
 * (non-shared) state when no provider is mounted, e.g. in Storybook.
 */
export function useDarkMode(): DarkModeState {
    const contextState = useContext(DarkModeContext);
    const standaloneState = useDarkModeState();
    return contextState ?? standaloneState;
}
